import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSuitabilityAssessments } from '../api';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import { ChevronRight, CheckCircle, AlertTriangle, XCircle, Search, BarChart2 } from 'lucide-react';

const RESULT_META = {
  SUITABLE:   { bg: '#F8FAFC', text: '#166534', border: '#DCFCE7', label: 'Suitable', icon: CheckCircle },
  UNSUITABLE: { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA', label: 'Unsuitable', icon: XCircle },
  BORDERLINE: { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A', label: 'Borderline', icon: AlertTriangle },
};

const ACTION_META = {
  PROCEED:                { bg: '#F8FAFC', text: '#166534', border: '#DCFCE7' },
  LEGAL_ALERT:            { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA' },
  MANUAL_REVIEW:          { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
  ENHANCED_DUE_DILIGENCE: { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
  ESCALATE_TO_MLRO:       { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA' },
};

const Badge = ({ label, meta, theme }) => (
  <span style={{
    background: meta?.bg || theme.bgInput,
    color: meta?.text || theme.textSecondary,
    border: `1px solid ${meta?.border || theme.border}`,
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  }}>
    {label || '-'}
  </span>
);

const Suitability = () => {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { theme } = useTheme();

  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    getSuitabilityAssessments()
      .then(res => setAssessments(res.data.assessments || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = assessments.filter(a => {
    if (filter && a.suitability_result !== filter) return false;

    const q = search.toLowerCase();
    if (!q) return true;

    return (
      a.pseudo_id?.toLowerCase().includes(q) ||
      a.fund_name?.toLowerCase().includes(q) ||
      a.fund_manager_name?.toLowerCase().includes(q) ||
      a.risk_profile?.toLowerCase().includes(q)
    );
  });

  const counts = {
    SUITABLE: assessments.filter(a => a.suitability_result === 'SUITABLE').length,
    BORDERLINE: assessments.filter(a => a.suitability_result === 'BORDERLINE').length,
    UNSUITABLE: assessments.filter(a => a.suitability_result === 'UNSUITABLE').length,
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: `1px solid ${theme.border}`,
    fontSize: 13,
    color: theme.textPrimary,
    background: theme.bgInput,
    boxSizing: 'border-box',
    outline: 'none',
  };

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 24,
        flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.textPrimary }}>
            Suitability Assessments
          </h1>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: theme.textSecondary }}>
            MiFID II Article 25 suitability results and compliance decisions.
          </p>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 10px',
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          background: theme.bgCard,
          boxShadow: theme.shadowSm,
        }}>
          <BarChart2 size={15} color={theme.textSecondary} />
          <span style={{ fontSize: 12, fontWeight: 600, color: theme.textSecondary }}>
            {assessments.length} total
          </span>
        </div>
      </div>

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        marginBottom: 16,
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          borderBottom: `1px solid ${theme.border}`,
        }}>
          {[
            ['Total', assessments.length, ''],
            ['Suitable', counts.SUITABLE, 'SUITABLE'],
            ['Borderline', counts.BORDERLINE, 'BORDERLINE'],
            ['Unsuitable', counts.UNSUITABLE, 'UNSUITABLE'],
          ].map(([label, value, key], idx) => {
            const active = filter === key;
            const danger = key === 'UNSUITABLE' && value > 0;
            const warning = key === 'BORDERLINE' && value > 0;

            return (
              <button
                key={label}
                onClick={() => setFilter(active ? '' : key)}
                style={{
                  textAlign: 'left',
                  padding: '16px 18px',
                  background: active ? theme.bgInput : 'transparent',
                  border: 'none',
                  borderRight: !isMobile && idx < 3 ? `1px solid ${theme.border}` : 'none',
                  cursor: 'pointer',
                }}
              >
                <p style={{ margin: 0, fontSize: 12, color: theme.textTertiary, fontWeight: 600 }}>
                  {label}
                </p>
                <p style={{
                  margin: '6px 0 0',
                  fontSize: 24,
                  fontWeight: 700,
                  color: danger ? '#991B1B' : warning ? '#92400E' : theme.textPrimary,
                  lineHeight: 1,
                }}>
                  {value}
                </p>
              </button>
            );
          })}
        </div>

        <div style={{
          padding: '14px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <div style={{ position: 'relative', width: isMobile ? '100%' : 340 }}>
            <Search size={14} color={theme.textTertiary} style={{
              position: 'absolute',
              left: 11,
              top: '50%',
              transform: 'translateY(-50%)',
            }} />
            <input
              type="text"
              placeholder="Search investor, fund, risk profile"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 34 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }}>
            {[
              ['', 'All'],
              ['SUITABLE', 'Suitable'],
              ['BORDERLINE', 'Borderline'],
              ['UNSUITABLE', 'Unsuitable'],
            ].map(([value, label]) => {
              const active = filter === value;
              return (
                <button key={label} onClick={() => setFilter(value)} style={{
                  padding: '7px 10px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: `1px solid ${active ? '#CBD5E1' : theme.border}`,
                  background: active ? '#F8FAFC' : theme.bgInput,
                  color: active ? theme.textPrimary : theme.textSecondary,
                  whiteSpace: 'nowrap',
                }}>
                  {label}
                </button>
              );
            })}
          </div>

          <span style={{ fontSize: 12, color: theme.textTertiary, whiteSpace: 'nowrap' }}>
            {filtered.length} of {assessments.length}
          </span>
        </div>
      </div>

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px',
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
        }}>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: theme.textPrimary }}>
              Assessment Register
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textTertiary }}>
              AI-assisted decisions, recommended actions, and risk context
            </p>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary, fontSize: 14 }}>
            Loading assessments...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: theme.textPrimary }}>
              No assessments found
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>
              Try a different search or result filter.
            </p>
          </div>
        ) : isMobile ? (
          <div style={{ display: 'grid', gap: 8, padding: 10 }}>
            {filtered.map(a => {
              const result = RESULT_META[a.suitability_result];
              const Icon = result?.icon;

              return (
                <button
                  key={a.id}
                  onClick={() => navigate(`/suitability/${a.id}`, { state: { assessment: a } })}
                  style={{
                    textAlign: 'left',
                    background: theme.bgInput,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 10,
                    padding: 13,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>
                        {a.pseudo_id}
                      </p>
                      <p style={{ margin: '3px 0 0', fontSize: 11, color: theme.textSecondary }}>
                        {a.fund_name} · {a.fund_manager_name}
                      </p>
                    </div>
                    <Badge label={result?.label || a.suitability_result} meta={result} theme={theme} />
                  </div>

                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <Badge label={a.recommended_action?.replace(/_/g, ' ')} meta={ACTION_META[a.recommended_action]} theme={theme} />
                    <Badge label={a.risk_profile} theme={theme} />
                    {a.inconsistency_count_high > 0 && (
                      <Badge label={`${a.inconsistency_count_high} high inconsistencies`} meta={RESULT_META.UNSUITABLE} theme={theme} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: theme.bgBase, borderBottom: `1px solid ${theme.border}` }}>
                {['Investor', 'Result', 'Action', 'Risk Profile', 'Assessed', ''].map(h => (
                  <th key={h} style={{
                    padding: '11px 16px',
                    textAlign: 'left',
                    fontSize: 10,
                    fontWeight: 600,
                    color: theme.textTertiary,
                    textTransform: 'uppercase',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, idx) => {
                const result = RESULT_META[a.suitability_result];
                const Icon = result?.icon;

                return (
                  <tr
                    key={a.id}
                    onClick={() => navigate(`/suitability/${a.id}`, { state: { assessment: a } })}
                    style={{
                      borderBottom: idx < filtered.length - 1 ? `1px solid ${theme.border}` : 'none',
                      cursor: 'pointer',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = theme.bgCardHover}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '14px 16px' }}>
                      <div>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>
                          {a.pseudo_id}
                        </p>
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: theme.textTertiary }}>
                          {a.fund_name} · {a.fund_manager_name}
                        </p>
                      </div>
                    </td>

                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        {Icon && <Icon size={14} color={result.text} />}
                        <Badge label={result?.label || a.suitability_result} meta={result} theme={theme} />
                      </div>
                    </td>

                    <td style={{ padding: '14px 16px' }}>
                      <Badge label={a.recommended_action?.replace(/_/g, ' ')} meta={ACTION_META[a.recommended_action]} theme={theme} />
                    </td>

                    <td style={{ padding: '14px 16px' }}>
                      <Badge label={a.risk_profile} theme={theme} />
                    </td>

                    <td style={{ padding: '14px 16px', fontSize: 12, color: theme.textSecondary }}>
                      {new Date(a.assessed_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>

                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      <ChevronRight size={15} color={theme.textTertiary} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Suitability;
